import { useMemo, useState } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Repeat,
  Sparkles,
} from "lucide-react";
import { Button, EmptyState, Skeleton } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { cn } from "@/design-system/lib/utils";
import { useUserTimeZone } from "@/utils/datetime";
import { formatTimeInTz, isoDateInTz } from "@/design-system/lib/format";
import { zoneCaption } from "@/lib/appointmentZones";
import type { AppointmentDto } from "@/api/appointments";
import { appointmentDayZone } from "./lib/tz";
import { useAppointments } from "./hooks/useAppointments";
import { AppointmentFormModal } from "./components/AppointmentFormModal";
import { AppointmentDayDrawer } from "./components/AppointmentDayDrawer";
import { AiCreateAppointmentPanel } from "./components/AiCreateAppointmentPanel";

type CalendarView = "month" | "week" | "day";

const VIEWS: { key: CalendarView; label: string }[] = [
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
];

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ---------------------------------------------------------------------------
// Pure date math — native Date only, computed on the wall calendar (UTC noon
// anchors avoid DST/offset edge cases since we only ever read Y/M/D).
// ---------------------------------------------------------------------------

/** A UTC-noon Date for the given calendar Y/M/D (1-based month). */
function ymd(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month, day, 12, 0, 0));
}

function startOfDay(d: Date): Date {
  return ymd(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function addDays(d: Date, n: number): Date {
  return ymd(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n);
}

/** Sunday on/before `d`. */
function startOfWeek(d: Date): Date {
  return addDays(d, -d.getUTCDay());
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The 6×7 grid of days covering the month that contains `cursor`. */
function monthGrid(cursor: Date): Date[] {
  const first = ymd(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1);
  const start = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** The visible [from, to) instant window for the current view. */
function rangeFor(view: CalendarView, cursor: Date): { from: Date; to: Date } {
  if (view === "month") {
    const grid = monthGrid(cursor);
    return { from: grid[0], to: addDays(grid[grid.length - 1], 1) };
  }
  if (view === "week") {
    const start = startOfWeek(cursor);
    return { from: start, to: addDays(start, 7) };
  }
  const start = startOfDay(cursor);
  return { from: start, to: addDays(start, 1) };
}

function periodLabel(view: CalendarView, cursor: Date): string {
  if (view === "month") {
    return cursor.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "long",
      year: "numeric",
    });
  }
  if (view === "week") {
    const start = startOfWeek(cursor);
    const end = addDays(start, 6);
    const startLabel = start.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    });
    const endLabel = end.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startLabel} – ${endLabel}`;
  }
  return cursor.toLocaleDateString("en-US", {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function CalendarPage() {
  const tz = useUserTimeZone();
  const [view, setView] = useState<CalendarView>("month");
  const [cursor, setCursor] = useState<Date>(() => startOfDay(todayInTz(tz)));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [defaultDate, setDefaultDate] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<AppointmentDto | undefined>(undefined);

  const todayIso = isoDay(startOfDay(todayInTz(tz)));

  const { from, to } = useMemo(() => rangeFor(view, cursor), [view, cursor]);
  const query = useAppointments({ from: from.toISOString(), to: to.toISOString() });

  // The caption is computed over the WHOLE visible range, so a March or November
  // grid — which contains a DST transition — drops the offset and names the zone
  // alone. One offset printed over a month that shifts is a false statement about
  // half the cells under it.
  const zoneNote = zoneCaption(tz, from, to);

  // Group the fetched appointments into day cells.
  //
  // 🔴 THE ZONE IS PER-APPOINTMENT, NOT PER-GRID. A timed appointment belongs on
  // the day it happens where the VIEWER is; an all-day one belongs on the day
  // its author picked, which does not move for anybody. `appointmentDayZone`
  // decides which, and reading every row in the viewer's zone — which is what
  // this did — put a westward viewer's all-day rows one cell early.
  const byDay = useMemo(() => {
    const map = new Map<string, AppointmentDto[]>();
    for (const appt of query.data ?? []) {
      const key = isoDateInTz(appt.startAt, appointmentDayZone(appt, tz));
      (map.get(key) ?? map.set(key, []).get(key)!).push(appt);
    }
    // Keep each day ordered by start time.
    for (const list of map.values()) {
      list.sort((a, b) => a.startAt.localeCompare(b.startAt));
    }
    return map;
  }, [query.data, tz]);

  const step = (dir: 1 | -1) => {
    if (view === "month") {
      setCursor((c) => ymd(c.getUTCFullYear(), c.getUTCMonth() + dir, 1));
    } else if (view === "week") {
      setCursor((c) => addDays(c, 7 * dir));
    } else {
      setCursor((c) => addDays(c, dir));
    }
  };

  const goToday = () => setCursor(startOfDay(todayInTz(tz)));

  const openDay = (dayIso: string) => setSelectedDay(dayIso);

  const openNew = (dayIso?: string) => {
    setDefaultDate(dayIso ?? todayIso);
    setCreating(true);
  };

  const openEdit = (appt: AppointmentDto) => {
    // Only the owner can edit. An invited attendee who clicks a chip gets the
    // read-only day detail (with accept/decline) instead of an edit form that
    // would 403 on save. The day drawer gates edit/cancel on ownership itself,
    // so this also covers its owner-only onEdit hook.
    if (!appt.isOwner) {
      setEditing(undefined);
      // Same rule as `byDay` above, and it has to be: this looks the row's own
      // cell back up by key, so a zone that disagreed would open an empty drawer
      // on a chip the user had just clicked.
      setSelectedDay(isoDateInTz(appt.startAt, appointmentDayZone(appt, tz)));
      return;
    }
    setSelectedDay(null);
    setEditing(appt);
  };

  const drawerAppointments = selectedDay ? byDay.get(selectedDay) ?? [] : [];

  return (
    <div>
      <PageHeader
        eyebrow="Calendar"
        title="Calendar"
        description={
          zoneNote
            ? `Your appointments and the meetings you're invited to. ${zoneNote}.`
            : "Your appointments and the meetings you're invited to."
        }
        actions={
          <div className="flex items-center gap-space-2">
            <Button variant="secondary" type="button" onClick={() => setAiOpen(true)}>
              <Sparkles size={16} strokeWidth={2} aria-hidden />
              AI Create+
            </Button>
            <Button onClick={() => openNew()} type="button">
              <Plus size={16} strokeWidth={2} aria-hidden />
              New appointment
            </Button>
          </div>
        }
      />

      <div className="mb-space-4 flex flex-wrap items-center justify-between gap-space-3">
        <div className="flex items-center gap-space-2">
          <Button
            variant="icon"
            size="icon"
            type="button"
            aria-label="Previous"
            onClick={() => step(-1)}
          >
            <ChevronLeft size={18} strokeWidth={1.5} aria-hidden />
          </Button>
          <Button variant="secondary" size="sm" type="button" onClick={goToday}>
            Today
          </Button>
          <Button
            variant="icon"
            size="icon"
            type="button"
            aria-label="Next"
            onClick={() => step(1)}
          >
            <ChevronRight size={18} strokeWidth={1.5} aria-hidden />
          </Button>
          <div className="ml-space-2 flex items-center gap-space-2">
            <CalendarIcon size={18} strokeWidth={1.5} className="text-gold-shadow" aria-hidden />
            <span className="font-display font-semibold text-h3 text-onyx tracking-normal">
              {periodLabel(view, cursor)}
            </span>
          </div>
        </div>

        <div
          className="inline-flex rounded-md border border-border-light p-[2px]"
          role="tablist"
          aria-label="Calendar view"
        >
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              onClick={() => setView(v.key)}
              className={cn(
                "px-space-3 py-space-1 rounded-sm font-display text-label font-semibold uppercase tracking-label",
                "transition-colors duration-fast ease-standard",
                view === v.key
                  ? "bg-gold text-onyx"
                  : "bg-transparent text-gray-mid hover:text-onyx",
              )}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {query.isError ? (
        <EmptyState
          icon={CalendarIcon}
          title="Couldn't load your calendar"
          description="Something went wrong fetching appointments. Try again shortly."
        />
      ) : view === "month" ? (
        <MonthView
          cursor={cursor}
          tz={tz}
          todayIso={todayIso}
          byDay={byDay}
          loading={query.isLoading}
          onOpenDay={openDay}
          onChipClick={openEdit}
        />
      ) : (
        <AgendaView
          from={from}
          to={to}
          tz={tz}
          todayIso={todayIso}
          byDay={byDay}
          loading={query.isLoading}
          onOpenDay={openDay}
          onChipClick={openEdit}
          onAddOnDay={openNew}
        />
      )}

      <AppointmentDayDrawer
        open={selectedDay !== null}
        onClose={() => setSelectedDay(null)}
        dayIso={selectedDay ?? ""}
        appointments={drawerAppointments}
        onEdit={openEdit}
      />

      <AppointmentFormModal
        open={creating}
        onClose={() => setCreating(false)}
        defaultDate={defaultDate}
      />

      <AppointmentFormModal
        open={editing !== undefined}
        onClose={() => setEditing(undefined)}
        appointment={editing}
      />

      <AiCreateAppointmentPanel open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}

/** Today as a UTC-noon anchor for the calendar's wall-date math, in `tz`. */
function todayInTz(tz: string): Date {
  // isoDateInTz reads "now" in the user's zone → YYYY-MM-DD, which we re-anchor
  // to UTC noon so all subsequent date math stays offset-safe.
  const iso = isoDateInTz(new Date(), tz);
  const [y, m, d] = iso.split("-").map(Number);
  return ymd(y, m - 1, d);
}

// ---------------------------------------------------------------------------

function MonthView({
  cursor,
  tz,
  todayIso,
  byDay,
  loading,
  onOpenDay,
  onChipClick,
}: {
  cursor: Date;
  tz: string;
  todayIso: string;
  byDay: Map<string, AppointmentDto[]>;
  loading: boolean;
  onOpenDay: (dayIso: string) => void;
  onChipClick: (appt: AppointmentDto) => void;
}) {
  const grid = useMemo(() => monthGrid(cursor), [cursor]);
  const month = cursor.getUTCMonth();

  return (
    <div className="rounded-lg border border-border-light bg-white-pure overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border-light">
        {WEEKDAY_LABELS.map((w) => (
          <div
            key={w}
            className="px-space-2 py-space-2 text-center font-display text-label font-semibold uppercase tracking-label text-gray-mid"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {grid.map((day, i) => {
          const dayIso = isoDay(day);
          const inMonth = day.getUTCMonth() === month;
          const isToday = dayIso === todayIso;
          const appts = byDay.get(dayIso) ?? [];
          return (
            <button
              key={dayIso}
              type="button"
              onClick={() => onOpenDay(dayIso)}
              className={cn(
                "min-h-[7rem] text-left flex flex-col gap-space-1 p-space-2 border-border-light",
                "border-b border-r",
                i % 7 === 6 && "border-r-0",
                inMonth ? "bg-white-pure" : "bg-white-warm",
                "hover:bg-white-warm transition-colors duration-fast ease-standard",
              )}
            >
              <span
                className={cn(
                  "inline-flex h-6 w-6 items-center justify-center rounded-pill text-caption font-semibold",
                  isToday
                    ? "bg-gold text-onyx"
                    : inMonth
                      ? "text-onyx"
                      : "text-gray-mid",
                )}
              >
                {day.getUTCDate()}
              </span>
              {loading ? (
                <Skeleton className="h-4 w-3/4" />
              ) : (
                <span className="flex flex-col gap-[2px]">
                  {appts.slice(0, 3).map((appt) => (
                    <DayChip key={appt.id} appointment={appt} tz={tz} onClick={onChipClick} />
                  ))}
                  {appts.length > 3 && (
                    <span className="text-caption text-gray-mid">
                      +{appts.length - 3} more
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AgendaView({
  from,
  to,
  tz,
  todayIso,
  byDay,
  loading,
  onOpenDay,
  onChipClick,
  onAddOnDay,
}: {
  from: Date;
  to: Date;
  tz: string;
  todayIso: string;
  byDay: Map<string, AppointmentDto[]>;
  loading: boolean;
  onOpenDay: (dayIso: string) => void;
  onChipClick: (appt: AppointmentDto) => void;
  onAddOnDay: (dayIso: string) => void;
}) {
  const days = useMemo(() => {
    const out: Date[] = [];
    for (let d = from; d.getTime() < to.getTime(); d = addDays(d, 1)) {
      out.push(d);
    }
    return out;
  }, [from, to]);

  if (loading) {
    return (
      <div className="flex flex-col gap-space-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      {days.map((day) => {
        const dayIso = isoDay(day);
        const isToday = dayIso === todayIso;
        const appts = byDay.get(dayIso) ?? [];
        const label = day.toLocaleDateString("en-US", {
          timeZone: "UTC",
          weekday: "long",
          month: "short",
          day: "numeric",
        });
        return (
          <div
            key={dayIso}
            className={cn(
              "rounded-lg border bg-white-pure p-space-3",
              isToday ? "border-gold" : "border-border-light",
            )}
          >
            <div className="mb-space-2 flex items-center justify-between gap-space-2">
              <button
                type="button"
                onClick={() => onOpenDay(dayIso)}
                className="font-display font-semibold text-small text-onyx hover:text-gold-shadow"
              >
                {label}
                {isToday && (
                  <span className="ml-space-2 text-caption text-gold-shadow uppercase tracking-label">
                    Today
                  </span>
                )}
              </button>
              <Button
                variant="tertiary"
                size="sm"
                type="button"
                onClick={() => onAddOnDay(dayIso)}
              >
                <Plus size={14} strokeWidth={2} aria-hidden />
                Add
              </Button>
            </div>
            {appts.length === 0 ? (
              <p className="m-0 text-caption text-gray-mid">No appointments.</p>
            ) : (
              <ul className="flex flex-col gap-[2px] m-0 p-0 list-none">
                {appts.map((appt) => (
                  <li key={appt.id}>
                    <DayChip appointment={appt} tz={tz} onClick={onChipClick} expanded />
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DayChip({
  appointment,
  tz,
  onClick,
  expanded,
}: {
  appointment: AppointmentDto;
  tz: string;
  onClick: (appt: AppointmentDto) => void;
  expanded?: boolean;
}) {
  const cancelled = appointment.status === "Cancelled";
  const timeLabel = appointment.isAllDay
    ? "All day"
    : formatTimeInTz(appointment.startAt, tz);
  // Every occurrence of a series stays its OWN row here — the month grid, the
  // week agenda and the day agenda all bucket by `isoDateInTz(startAt)`, and a
  // calendar that hid the other occurrences of a standing call would be lying
  // about the days they fall on. The glyph is the only thing that changes: it
  // says "this one repeats" without moving anything.
  const repeats = Boolean(appointment.seriesId);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(appointment);
      }}
      title={`${timeLabel} · ${appointment.title}`}
      className={cn(
        "w-full text-left rounded-sm px-space-1 py-[2px] text-caption truncate",
        "transition-colors duration-fast ease-standard",
        cancelled
          ? "bg-white-warm text-gray-mid line-through hover:bg-border-light"
          : "bg-gold/15 text-onyx hover:bg-gold/25",
        expanded && "py-space-1",
      )}
    >
      <span className="font-semibold">{timeLabel}</span>
      {repeats && (
        <Repeat
          size={11}
          strokeWidth={2}
          role="img"
          aria-label="Repeats"
          className="ml-space-1 inline-block shrink-0 align-middle"
        />
      )}
      <span className="ml-space-1">{appointment.title}</span>
    </button>
  );
}
